
import { useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const AboutPage = () => {
  useEffect(() => {
    // Update page title and meta description for about page
    document.title = 'About WOTECH - Enterprise IT Solutions Company | Delaware USA';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about WOTECH, a leading enterprise IT solutions provider specializing in ManageEngine, AutomMate, and Zoho One implementations since 2008.');
    }

    // Add structured data for AboutPage
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About WOTECH",
      "description": "Learn about WOTECH's mission, vision, team, and expertise in enterprise IT solutions.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`,
      "mainEntity": {
        "@type": "Organization",
        "name": "WOTECH",
        "foundingDate": "2008",
        "description": "Enterprise IT solutions and automation services provider",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "Delaware",
          "addressCountry": "US"
        }
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "About",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const stats = [
    { number: '20+', label: 'Enterprise Clients' },
    { number: '15+', label: 'Years Experience' },
    { number: '5+', label: 'Certified Consultants' },
    { number: '30+', label: 'Solution Portfolio' }
  ];

  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'We continuously explore the latest technologies to deliver future-ready solutions.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Security',
      description: 'We ensure complete data security and compliance with international standards like ISO 27001.'
    },
    {
      icon: 'ri-team-line',
      title: 'Partnership',
      description: 'We build long-term relationships with our clients through trust and exceptional service delivery.'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Excellence',
      description: 'We deliver measurable business impact through quality solutions and continuous improvement.'
    }
  ];

  const team = [
    {
      name: 'John Smith',
      position: 'CEO & Founder',
      image: 'https://public.readdy.ai/ai/img_res/50186e7b348c7c6c3d95b860a5e59aa0.jpg',
      description: '20+ years of experience in IT industry, expert in ManageEngine and AutomMate solutions.'
    },
    {
      name: 'Sarah Johnson',
      position: 'Technical Director',
      image: 'https://readdy.ai/api/search-image?query=Professional%20female%20technology%20director%2C%20confident%20woman%20in%20business%20attire%2C%20modern%20tech%20office%20environment%2C%20corporate%20headshot%20style&width=300&height=400&seq=cto-sarah&orientation=portrait',
      description: 'ITIL Expert certified, 15+ years of experience in ITSM and ITOM implementations.'
    },
    {
      name: 'Michael Brown',
      position: 'Automation Specialist',
      image: 'https://readdy.ai/api/search-image?query=Professional%20automation%20specialist%2C%20young%20man%20in%20casual%20business%20attire%2C%20technology%20workspace%20background%2C%20corporate%20headshot%20style&width=300&height=400&seq=specialist-michael&orientation=portrait',
      description: 'Expert in RPA and business process automation, AutomMate certified consultant.'
    },
    {
      name: 'Emily Davis',
      position: 'Customer Success Manager',
      image: 'https://readdy.ai/api/search-image?query=Professional%20customer%20success%20manager%2C%20confident%20woman%20in%20modern%20business%20attire%2C%20friendly%20professional%20demeanor%2C%20office%20environment&width=300&height=400&seq=csm-emily&orientation=portrait',
      description: '12+ years of experience in customer relations and project management.'
    }
  ];

  const certifications = [
    { name: 'ManageEngine Authorized Partner', icon: 'ri-award-line' },
    { name: 'AutomMate Certified Partner', icon: 'ri-medal-line' },
    { name: 'Zoho One Implementation Partner', icon: 'ri-trophy-line' },
    { name: 'ITIL Foundation Certified', icon: 'ri-shield-check-line' },
    { name: 'ISO 27001 Compliant', icon: 'ri-security-line' },
    { name: 'Microsoft Partner', icon: 'ri-star-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                About WOTECH
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                We lead digital transformation journeys for medium and large enterprises with comprehensive IT solutions and automation services.
              </p>
              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <i className="ri-calendar-line text-blue-600 text-xl mr-2"></i>
                  <span className="text-gray-700 font-semibold">Since 2008</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-map-pin-line text-blue-600 text-xl mr-2"></i>
                  <span className="text-gray-700 font-semibold">Delaware, USA</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20IT%20company%20office%2C%20professional%20technology%20workspace%2C%20team%20collaboration%20area%2C%20contemporary%20office%20design%20with%20computers%20and%20technology%20equipment%2C%20business%20environment&width=600&height=500&seq=wotech-office&orientation=landscape"
                alt="WOTECH Office"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Mission & Vision</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We simplify complex IT environments by integrating service management, automation, and analytics solutions into a single operational ecosystem.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
                <div className="flex items-center mb-4">
                  <i className="ri-target-line text-blue-600 text-3xl mr-4"></i>
                  <h3 className="text-2xl font-bold text-gray-900">Our Mission</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Through our deep partnerships with ManageEngine, AutomMate, and Zoho One, we enable businesses to achieve measurable gains in efficiency, governance, and scalability.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex items-center mb-4">
                  <i className="ri-eye-line text-teal-600 text-3xl mr-4"></i>
                  <h3 className="text-2xl font-bold text-gray-900">Our Vision</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  To be the leading IT solution partner, serving as a trusted guide in enterprises' digital transformation journey and perfectly integrating technology with business processes.
                </p>
              </div>
            </div>
            
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Digital%20transformation%20concept%2C%20modern%20technology%20integration%2C%20business%20process%20automation%20visualization%2C%20enterprise%20IT%20solutions%2C%20futuristic%20business%20environment%20with%20data%20analytics%20and%20automation&width=600&height=500&seq=digital-transformation&orientation=portrait"
                alt="Digital Transformation"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The core values that guide our way of doing business and customer relationships.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center group hover:transform hover:scale-105 transition-all duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:shadow-lg transition-shadow">
                  <i className={`${value.icon} text-3xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              With our experienced and certified experts, we guarantee success in your projects.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <img 
                  src={member.image}
                  alt={member.name}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h3>
                  <p className="text-blue-600 font-medium mb-3">{member.position}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Certifications & Partnerships</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our official partnerships and certifications with leading technology providers in the industry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-teal-50 p-6 rounded-xl border border-blue-100 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                    <i className={`${cert.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{cert.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Domains */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Technology Domains</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our specialized technology areas and solution portfolio.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                'IT Service Management (ITSM)',
                'IT Operations Management (ITOM)',
                'Endpoint & Asset Management',
                'Business Process Automation',
                'Active Directory & Identity Management',
                'Monitoring & Performance Analytics',
                'IT Workflow Integration',
                'Cloud and On-Premise Deployments'
              ].map((domain, index) => (
                <div key={index} className="flex items-center group hover:bg-blue-50 p-3 rounded-lg transition-colors">
                  <i className="ri-check-line text-teal-500 mr-3 text-lg"></i>
                  <span className="text-gray-700 font-medium group-hover:text-blue-700 transition-colors">{domain}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-teal-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Work with Us on Your Projects?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Let's take your IT infrastructure to the next level with our experienced team and proven solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#contact" 
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get In Touch
            </a>
            <a 
              href="/services" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore Our Services
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
